/**
 * Iter 250 · Super-admin Platform settings.
 *
 * Two features:
 *   1. Toggle `restrict_workspace_creation` at platform level.
 *   2. Grant/revoke `platform.workspace.create` to specific users so they
 *      can still create workspaces while the restriction is on.
 *
 * Hidden entirely from non super-admins (checks /me/platform-grants).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Shield, ShieldAlert, Search, Plus, Minus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "@/lib/apiError";

export default function PlatformSettingsSection() {
  const [me, setMe] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const grants = await api.get("/me/platform-grants");
      setMe(grants.data);
      if (grants.data.is_super_admin) {
        const s = await api.get("/platform/settings");
        setSettings(s.data);
      }
    } catch {
      // Not a super admin — silent fail.
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (checked) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/platform/settings", { restrict_workspace_creation: checked });
      setSettings(data);
      toast.success(checked ? "Workspace creation restricted" : "Workspace creation open to all");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    } finally { setSaving(false); }
  };

  const searchUsers = async () => {
    if (!search.trim()) return;
    try {
      const { data } = await api.get(`/admin/users/search?q=${encodeURIComponent(search)}&limit=20`);
      setUsers(Array.isArray(data) ? data : (data?.rows || []));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Search failed"));
    }
  };

  const grant = async (uid, action) => {
    try {
      await api.post("/platform/grants", { user_id: uid, grant: "platform.workspace.create", action });
      toast.success(action === "add" ? "Grant added" : "Grant removed");
      await searchUsers();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  if (loading) return null;
  if (!me?.is_super_admin) return null;

  return (
    <div className="border border-border p-6 space-y-5" data-testid="platform-settings-section">
      <div className="flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-primary" />
        <div>
          <div className="label-eyebrow">Platform · Super Admin controls</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Global settings that apply to every workspace on this platform. Only visible to super-administrators.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between border border-border p-4">
        <div>
          <div className="font-semibold text-sm">Restrict workspace creation</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            When ON, only super-admins and users explicitly granted <code className="font-mono">platform.workspace.create</code> can create new workspaces.
          </p>
        </div>
        <Switch
          checked={!!settings?.restrict_workspace_creation}
          disabled={saving}
          onCheckedChange={toggle}
          data-testid="platform-restrict-toggle"
        />
      </div>

      <div className="border border-border p-4">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-primary" />
          <div className="font-semibold text-sm">Workspace-creation grantees</div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Add or revoke the <code className="font-mono">platform.workspace.create</code> grant for individual users.
          When the restriction is ON above, only these users (and super-admins) can create workspaces.
        </p>
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setShowSearch(true); searchUsers(); } }}
              placeholder="Search users by email or name…"
              className="pl-7 h-9 text-sm"
              data-testid="platform-grants-search"
            />
          </div>
          <Button
            type="button"
            onClick={() => { setShowSearch(true); searchUsers(); }}
            className="h-9 rounded-none"
            data-testid="platform-grants-search-btn"
          >Search</Button>
        </div>
        {showSearch && users.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-4">No users found. Try a different email or name.</div>
        )}
        {users.length > 0 && (
          <div className="border border-border">
            {users.map((u) => {
              const hasGrant = (u.platform_grants || []).includes("platform.workspace.create");
              return (
                <div key={u.id} className="grid grid-cols-[1fr_120px_120px] gap-2 px-3 py-2 border-b border-border last:border-b-0 items-center text-sm"
                      data-testid={`platform-grants-row-${u.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{u.name || u.email}</div>
                    {u.name && u.email && <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>}
                  </div>
                  <div className="text-[11px]">
                    {u.is_super_admin ? (
                      <span className="chip chip-success text-[10px]">super-admin</span>
                    ) : hasGrant ? (
                      <span className="chip chip-success text-[10px]">granted</span>
                    ) : (
                      <span className="chip text-[10px] text-muted-foreground">no grant</span>
                    )}
                  </div>
                  <div className="text-right">
                    {u.is_super_admin ? (
                      <span className="text-[10px] text-muted-foreground italic">auto</span>
                    ) : hasGrant ? (
                      <button type="button" onClick={() => grant(u.id, "remove")}
                                className="text-[11px] px-2 py-1 border border-border hover:border-rose-500 hover:text-rose-600 flex items-center gap-1 ml-auto"
                                data-testid={`platform-grants-remove-${u.id}`}>
                        <Minus className="w-3 h-3" /> Revoke
                      </button>
                    ) : (
                      <button type="button" onClick={() => grant(u.id, "add")}
                                className="text-[11px] px-2 py-1 border border-primary text-primary hover:bg-primary/10 flex items-center gap-1 ml-auto"
                                data-testid={`platform-grants-add-${u.id}`}>
                        <Plus className="w-3 h-3" /> Grant
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
